import Pusher from 'pusher-js'
import Echo from 'laravel-echo'

;(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher

export interface SellerSupportMessageEvent {
  ticket_id: number
  ticket_subject: string
  requester?: {
    type: string
    name: string
    email?: string | null
  }
  message: {
    id: number
    message: string
    is_admin: boolean
    message_type?: string
    author_type?: string
    author_name?: string
    created_at: string
  }
}

export interface SellerSupportStatusChangedEvent {
  ticket_id: number
  subject: string
  old_status: string
  new_status: string
  updated_at: string
}

let echoInstance: any = null

const resolveApiOrigin = (): string => {
  const configuredOrigin = import.meta.env.VITE_API_ORIGIN?.replace(/\/+$/, '')
  if (configuredOrigin) {
    return configuredOrigin
  }

  const configuredApiUrl = import.meta.env.VITE_API_URL
  if (configuredApiUrl) {
    try {
      return new URL(configuredApiUrl, window.location.origin).origin
    } catch {
      return configuredApiUrl.replace(/\/+$/, '')
    }
  }

  return window.location.origin.replace(/\/+$/, '')
}

const resolveReverbHost = (apiOrigin: string): string => {
  if (import.meta.env.VITE_REVERB_HOST) {
    return import.meta.env.VITE_REVERB_HOST
  }

  try {
    return new URL(apiOrigin).hostname
  } catch {
    return '127.0.0.1'
  }
}

const API_ORIGIN = resolveApiOrigin()
const BROADCAST_AUTH_URL = `${API_ORIGIN}/api/broadcasting/auth`
const REVERB_HOST = resolveReverbHost(API_ORIGIN)
const REVERB_APP_KEY = import.meta.env.VITE_REVERB_APP_KEY || 'local'
const REVERB_SCHEME = (import.meta.env.VITE_REVERB_SCHEME || (API_ORIGIN.startsWith('https://') ? 'https' : 'http')).toLowerCase()
const REVERB_FORCE_TLS = REVERB_SCHEME === 'https'
const REVERB_PORT = Number(import.meta.env.VITE_REVERB_PORT || 8080)
const REVERB_WS_PORT = Number(import.meta.env.VITE_REVERB_WS_PORT || REVERB_PORT)

const getEcho = () => {
  if (!echoInstance) {
    const pusherAuthorizer = (channel: any) => ({
      authorize: (socketId: string, callback: any) => {
        const token = localStorage.getItem('seller_token')

        fetch(BROADCAST_AUTH_URL, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(async (response) => {
            const data = await response.json().catch(() => null)

            if (!response.ok || !data || typeof data !== 'object' || !('auth' in data)) {
              const reason = (data && typeof data === 'object' && 'message' in data)
                ? String((data as { message?: unknown }).message ?? '')
                : `Broadcast auth failed (${response.status})`

              callback(new Error(reason), null)
              return
            }

            callback(null, data)
          })
          .catch((error) => callback(error, null))
      },
    })

    echoInstance = new Echo({
      broadcaster: 'reverb',
      key: REVERB_APP_KEY,
      wsHost: REVERB_HOST,
      wsPort: REVERB_WS_PORT,
      wssPort: REVERB_WS_PORT,
      forceTLS: REVERB_FORCE_TLS,
      enabledTransports: ['ws', 'wss'],
      authorizer: pusherAuthorizer,
    })
  }

  return echoInstance
}

export const connectSellerReverb = () => {
  getEcho()
}

export const listenToSellerTicket = (
  ticketId: number,
  onMessage: (data: SellerSupportMessageEvent) => void,
  onStatusChange: (data: SellerSupportStatusChangedEvent) => void,
) => {
  const echo = getEcho()
  const channel = echo.private(`support.ticket.${ticketId}`)

  channel.listen('.message.received', onMessage)
  channel.listen('.ticket.status.changed', onStatusChange)

  return () => {
    echo.leave(`support.ticket.${ticketId}`)
  }
}

export const disconnectSellerReverb = () => {
  if (echoInstance) {
    try {
      echoInstance.disconnect()
    } catch {
      // Ignore disconnect transport errors.
    }

    echoInstance = null
  }
}
